"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { Typography } from "@/components/ui";

export type CaseStudyStickyCtaLink = {
  href: string;
  label: string;
};

export type CaseStudyStickyCtaProps = {
  /** Desktop (and optionally mobile) bar message */
  message?: string;
  primaryCta: CaseStudyStickyCtaLink;
  secondaryCta?: CaseStudyStickyCtaLink;
  /** Scroll depth (0–1) before the bar appears. Default 0.5 */
  scrollThreshold?: number;
  dataLead?: string;
  /** Hide the message on small screens. Default true */
  hideMessageOnMobile?: boolean;
  /** Primary button style. Default "accent" */
  primaryVariant?: "accent" | "primary";
  dismissAriaLabel?: string;
};

export function CaseStudyStickyCta({
  message = "Ready to see Pickcel in action?",
  primaryCta,
  secondaryCta,
  scrollThreshold = 0.5,
  dataLead = "case-study-demo",
  hideMessageOnMobile = true,
  primaryVariant = "accent",
  dismissAriaLabel = "Close",
}: CaseStudyStickyCtaProps) {
  const [pastThreshold, setPastThreshold] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const denom = document.body.scrollHeight - window.innerHeight;
      const depth = denom > 0 ? window.scrollY / denom : 0;
      setPastThreshold(depth > scrollThreshold);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [scrollThreshold]);

  if (!pastThreshold || dismissed) return null;

  const primaryClass =
    primaryVariant === "primary"
      ? "bg-primary text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
      : "bg-accent text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap";

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#12224F] border-t border-white/10 shadow-[0_-12px_40px_-20px_rgba(0,0,0,0.5)]"
      data-lead={dataLead}
    >
      <div className="max-w-[1240px] mx-auto px-5 md:px-10 py-3.5 flex items-center justify-between gap-5 flex-wrap">
        <Typography
          variant="body"
          as="span"
          className={`font-medium text-[#EAEFFB] ${hideMessageOnMobile ? "hidden md:block" : ""}`}
        >
          {message}
        </Typography>
        <div className="flex items-center gap-3 flex-wrap ml-auto">
          <Link href={primaryCta.href} className={primaryClass}>
            {primaryCta.label}
          </Link>
          {secondaryCta ? (
            <Link
              href={secondaryCta.href}
              className="border border-white/30 text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:bg-white/5 transition-colors whitespace-nowrap"
            >
              {secondaryCta.label}
            </Link>
          ) : null}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-[#8FA0C6] text-2xl leading-none px-1"
            aria-label={dismissAriaLabel}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
